<template>
  <div>
    <top-menu @logout="$emit('logout')" />
    <v-container
      fluid
      class="px-8 py-4"
    >
      <face-sheet-search-form
        v-model:search-form="searchForm"
        :coupon-name-options="couponNameOptions"
        :status-options="statusOptions"
        :unhandled-alert-options="unhandledAlertOptions"
        :history-condition-options="historyConditionOptions"
        :is-searching="isSearching"
        @search="applySearch"
        @clear="clearSearch"
      />
      <face-sheet-search-results
        v-model:page="page"
        :headers="faceSheetTableHeaders"
        :items="faceSheetList"
        :items-per-page="itemsPerPage"
        :capped="isResultCapped"
        :max-results="maxResults"
      />
    </v-container>
    <v-snackbar
      v-model="permissionDeniedSnackbar"
      :timeout="-1"
      color="error"
      location="top"
    >
      {{ permissionDeniedMessage }}
    </v-snackbar>
    <v-snackbar
      v-model="genericErrorSnackbar"
      :timeout="6000"
      color="error"
      location="bottom right"
    >
      {{ genericErrorMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import TopMenu from "@/vue/components/shared/TopMenu.vue";
import couponsApi from "@/vue/api/coupons";
import { historyConditionOptions, statusOptions, unhandledAlertOptions } from "../model/searchOptions";
import { persistSearchForm, readPersistedSearchForm } from "../model/searchSession";
import { faceSheetTableHeaders } from "../model/tableHeaders";
import FaceSheetSearchForm from "./FaceSheetSearchForm.vue";
import FaceSheetSearchResults from "./FaceSheetSearchResults.vue";
import { createFaceSheetsActions, GENERIC_ERROR_MESSAGE, isForbiddenError, PERMISSION_DENIED_MESSAGE } from "@/vue/entities/face-sheet";

const createInitialSearchForm = () => ({
  userId: "",
  familyNameKana: "",
  firstNameKana: "",
  postalCode: "",
  couponName: null,
  userBirthday: "",
  childBirthday: "",
  statuses: ["要対応"],
  unhandledAlerts: [],
  historyConditions: []
});

const createEmptySearchForm = () => ({
  userId: "",
  familyNameKana: "",
  firstNameKana: "",
  postalCode: "",
  couponName: null,
  userBirthday: "",
  childBirthday: "",
  statuses: [],
  unhandledAlerts: [],
  historyConditions: []
});

const cloneSearchForm = (searchForm) => ({
  ...searchForm,
  statuses: [...searchForm.statuses],
  unhandledAlerts: [...searchForm.unhandledAlerts],
  historyConditions: [...searchForm.historyConditions]
});

const normalizeText = (value) => String(value ?? "").trim();

// 検索フォームの値を、サーバー側検索用のクエリパラメータ（snake_case）へ変換する。
// 空の条件は送らない。絞り込みはすべてサーバー側（DB）で行う。
const buildSearchParams = (form) => {
  const params = {};

  if (normalizeText(form.userId)) params.user_id = normalizeText(form.userId);
  if (normalizeText(form.familyNameKana)) params.family_name_kana = normalizeText(form.familyNameKana);
  if (normalizeText(form.firstNameKana)) params.first_name_kana = normalizeText(form.firstNameKana);
  if (normalizeText(form.postalCode)) params.postal_code = normalizeText(form.postalCode);
  if (normalizeText(form.couponName)) params.coupon = normalizeText(form.couponName);
  if (normalizeText(form.userBirthday)) params.birthday = normalizeText(form.userBirthday);
  if (normalizeText(form.childBirthday)) params.child_birthday = normalizeText(form.childBirthday);
  if (form.statuses.length > 0) params.statuses = [...form.statuses];
  if (form.unhandledAlerts.length > 0) params.unhandled_alerts = [...form.unhandledAlerts];
  if (form.historyConditions.length > 0) params.history_conditions = [...form.historyConditions];

  return params;
};

export default {
  name: "FaceSheetsTopPage",
  components: {
    FaceSheetSearchForm,
    FaceSheetSearchResults,
    TopMenu
  },
  beforeRouteLeave(to, from, next) {
    persistSearchForm(to.name === "faceSheetsDetail" ? this.searchForm : null);
    next();
  },
  emits: ["logout"],
  data() {
    const initialSearchForm = readPersistedSearchForm() || createInitialSearchForm();

    return {
      faceSheetsActions: null,
      faceSheetList: [],
      isResultCapped: false,
      isSearching: false,
      maxResults: 100,
      permissionDeniedSnackbar: false,
      permissionDeniedMessage: PERMISSION_DENIED_MESSAGE,
      genericErrorSnackbar: false,
      genericErrorMessage: GENERIC_ERROR_MESSAGE,
      searchForm: cloneSearchForm(initialSearchForm),
      page: 1,
      itemsPerPage: 50,
      faceSheetTableHeaders,
      couponNameOptions: [],
      statusOptions,
      unhandledAlertOptions,
      historyConditionOptions
    };
  },
  created() {
    this.faceSheetsActions = createFaceSheetsActions(undefined, () => this.showGenericError());
    this.loadCouponNames();
    this.loadFaceSheets(buildSearchParams(this.searchForm));
  },
  methods: {
    showGenericError() {
      this.genericErrorSnackbar = true;
    },
    async loadCouponNames() {
      try {
        this.couponNameOptions = await couponsApi.fetchCouponNames();
      } catch {
        this.couponNameOptions = [];
      }
    },
    async loadFaceSheets(params) {
      this.isSearching = true;
      try {
        const result = await this.faceSheetsActions.fetchList(params);
        this.faceSheetList = result?.items ?? [];
        this.isResultCapped = result?.capped ?? false;
      } catch (error) {
        this.faceSheetList = [];
        this.isResultCapped = false;
        if (isForbiddenError(error)) {
          this.redirectToTopWithoutPermission();
        }
      } finally {
        this.isSearching = false;
      }
    },
    redirectToTopWithoutPermission() {
      this.permissionDeniedSnackbar = true;
      window.setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    },
    applySearch() {
      this.page = 1;
      this.loadFaceSheets(buildSearchParams(this.searchForm));
    },
    clearSearch() {
      this.searchForm = createEmptySearchForm();
    }
  }
};
</script>
